import { AuthError } from "@azure/msal-browser";
import { has, isArray, isString, get } from "lodash-es";
import i18next from "i18next";

/// ******
/// This file contains custom Error (exception) classes and related helpers.
/// ******


export const isInstanceOfError = (exception: unknown): exception is Error => {
    return exception instanceof Error ? true : false;
}

export const isAuthError = (exception: unknown): exception is AuthError => {
    return typeof exception == 'object'
        && exception !== null
        && 'name' in exception && exception.name === 'AuthError'
        && 'errorMessage' in exception && typeof exception.errorMessage === 'string'
        && 'errorCode' in exception && typeof exception.errorCode === 'string';
}

/** This error conveys that user interaction is required to continue with current authentication flow. */
export class AuthInteractionRequiredError extends Error {}

/**
 * A serializable exception returned from backend related to validation of configuration objects.
 */
export type BackendValidationError = {

    /** Details of validation errors returned from backend. */
    readonly validationErrors: BackendValidationErrorDetail[];

    /** Original JSON representation of this validation error as received from backend. */
    readonly json: string;

    /** Error message */
    message: string | undefined;

}

/** Type guard for BackendValidationError */
export function isBackendValidationError(obj: unknown): obj is BackendValidationError {
    return has(obj, 'json') && isString(obj['json'])
        && has(obj, 'message') && (obj['message'] === undefined || isString(obj['message']))
        && has(obj, 'validationErrors') && isArray(obj['validationErrors'])
        && obj['validationErrors'].every(v => isBackendValidationErrorDetail(v));
}

/** Details related to a validation error received from backend. */
export type BackendValidationErrorDetail = {
    loc: (string | number)[];
    msg: string;
    type: string;
    ctx: unknown;
};

export const LOC_VALUE_BODY = 'body';

/** Type guard for BackendValidationErrorDetail */
export function isBackendValidationErrorDetail(obj: unknown): obj is BackendValidationErrorDetail {
    const castObj = obj as BackendValidationErrorDetail;
    return isArray(castObj.loc) && isString(castObj.msg) && isString(castObj.type);// && castObj.ctx !== undefined;
}

// TODO: does this need to be async?
/**
 * Converts backend validation error DTO into one or more UI location-specific error objects
 * that help pinpointing the exact location in UI of the context of the errors.
 * @param responseData the validation error JSON DTO from backend
 * @param originalJson the contouring customization JSON data object as a string that was sent to backend
 * @param message an optional error message that is attached to errors
 * @returns a BackendValidationError if input data matches expected DTO format or a generic 
 * Error otherwise
 */
export async function convertResponseToError(responseData: unknown, originalJson: string, message?: string): Promise<BackendValidationError | Error> {
    if (has(responseData, 'detail') && isArray(responseData.detail)) {
        const errors: BackendValidationErrorDetail[] = ((responseData.detail as unknown[]).map((d: unknown) => isBackendValidationErrorDetail(d) ? d as BackendValidationErrorDetail : undefined))
            .filter((e: BackendValidationErrorDetail | undefined) => e !== undefined);
        if (errors.length > 0) {
            // return a BackendValidationError
            return {
                message: message,
                validationErrors: errors,
                json: originalJson,
            };
        }
    }
    return new Error(message);
}

/**
 * A serializable error type representing a concurrent save conflict returned from backend.
 * The backend returns HTTP 400 with body: { detail: { message: string, conflicting_models: string[] } }
 */
export type ConcurrentSaveError = {
    message: string;
    conflictingModels: string[];
}

/** Type guard for detecting a concurrent save error shape from a backend response JSON. */
export function isConcurrentSaveError(obj: unknown): boolean {
    return has(obj, 'detail.message') && has(obj, 'detail.conflicting_models') && isArray(get(obj, 'detail.conflicting_models'));
}

/** Parses a backend response JSON into a ConcurrentSaveError, or returns undefined if it doesn't match. */
export function parseConcurrentSaveError(responseJson: unknown): ConcurrentSaveError | undefined {
    if (isConcurrentSaveError(responseJson)) {
        return {
            message: String(get(responseJson, 'detail.message')),
            conflictingModels: get(responseJson, 'detail.conflicting_models') as unknown as string[],
        };
    }
    return undefined;
}

/** Error wrapper class for concurrent save conflicts. Thrown when the backend detects another user has saved changes. */
export class ConcurrentSaveErrorWrapper extends Error {
    concurrentSaveError: ConcurrentSaveError;
    constructor(error: ConcurrentSaveError) {
        super(error.message);
        this.concurrentSaveError = error;
    }
}

/**
 * Try to extract an validation error message from a JSON DTO coming from backend,
 * or return a generic error message if parsing the DTO failed.
 */
export function getErrorMessageFromBackendValidationErrorJson(json: unknown): string {
    console.error(json);
    const errorMsg: string | undefined = has(json, 'detail') && isString(json.detail) ? json.detail : get(json, 'detail.toString()', undefined);
    return errorMsg ? `An error was received from backend: ${errorMsg}` : `An error was received from backend.`;
}

/** Returns true if the error message indicates a network connectivity issue. */
function isNetworkError(message: string): boolean {
    const lower = message.toLowerCase();
    return lower.includes('failed to fetch')
        || lower.includes('load failed')
        || lower.includes('networkerror');
}

/** Extracts a user-facing error message from an API error.
 *  Enhances generic network errors with actionable guidance. */
export function getApiErrorMessage(error: unknown, defaultMessage: string = 'Unknown error'): string {
    let problem: string;
    let message: string;

    if (isString(error)) {
        problem = 'Error';
        message = error;
    } else if (isInstanceOfError(error)) {
        problem = error.name ?? 'Error';
        message = error.message ?? defaultMessage;
    } else {
        problem = get(error, 'problem', 'Error');
        message = get(error, 'message', defaultMessage);
    }

    const base = `${problem}: ${message}`;

    if (isNetworkError(String(message))) {
        return `${base}. ${i18next.t('translation:error.networkError.guidance')}`;
    }

    return base;
}
